import type { Shape } from 'canvas-confetti';
import { useCallback } from 'react';

import { logger } from '@/lib/logger';

type ConfettiFire = typeof import('canvas-confetti');

type ConfettiConfigDefault = {
  variant: 'default';
};

type ConfettiConfigCannon = {
  variant: 'cannon';
  imageSrc: string;
  imageWidth?: number;
  imageHeight?: number;
  colors: string[];
};

/** Display-only confetti settings consumed directly by useConfetti. */
export type ConfettiDisplayConfig = ConfettiConfigDefault | ConfettiConfigCannon;

const cannonShapePromises = new Map<string, Promise<Shape | undefined>>();
const DEFAULT_CONFETTI_IMAGE_SIZE = 64;

const triggerDefaultConfetti = (confetti: ConfettiFire) => {
  const showDefaultConfetti = (originX: number, angle: number) => {
    confetti({
      particleCount: 60,
      angle,
      spread: 55,
      origin: { x: originX, y: 0.85 },
      startVelocity: 45,
      colors: ['#9b8cff', '#36e1aa', '#ffd166', '#ffffff'],
      gravity: 0.9,
      scalar: 0.9,
      disableForReducedMotion: true,
      zIndex: 9999,
    });
  };

  // Fire from left, right and then center after a short delay.
  showDefaultConfetti(0.1, 60);
  showDefaultConfetti(0.9, 120);
  setTimeout(() => showDefaultConfetti(0.5, 90), 200);
};

const getConfettiImageShape = async (config: ConfettiConfigCannon) => {
  const {
    imageSrc,
    imageHeight = DEFAULT_CONFETTI_IMAGE_SIZE,
    imageWidth = DEFAULT_CONFETTI_IMAGE_SIZE,
  } = config;
  const cacheKey = `${imageSrc}:${imageWidth}:${imageHeight}`;

  if (cannonShapePromises.has(cacheKey)) {
    return cannonShapePromises.get(cacheKey);
  }

  const imageToBitmap = async () => {
    if (typeof fetch !== 'function' || typeof createImageBitmap !== 'function') {
      return undefined;
    }

    try {
      const response = await fetch(imageSrc);
      if (!response.ok) {
        throw new Error(`Failed to fetch confetti image: ${response.status}`);
      }

      const blob = await response.blob();
      const bitmap = await createImageBitmap(blob, {
        resizeWidth: imageWidth,
        resizeHeight: imageHeight,
        resizeQuality: 'high',
      });

      const matrix = [1, 0, 0, 1, -imageWidth / 2, -imageHeight / 2];
      return { type: 'bitmap', bitmap, matrix } as unknown as Shape;
    } catch (err) {
      cannonShapePromises.delete(cacheKey);
      logger.error('Failed to load confetti image:', err);
      return undefined;
    }
  };

  const cannonShapePromise = imageToBitmap();
  cannonShapePromises.set(cacheKey, cannonShapePromise);

  return cannonShapePromise;
};

const triggerCannonConfetti = async (confetti: ConfettiFire, config: ConfettiConfigCannon) => {
  const { colors } = config;

  const imageShape = await getConfettiImageShape(config);

  const cannonDuration = 2000;
  const burstInterval = 80;
  const totalBursts = Math.floor(cannonDuration / burstInterval);

  const randomInRange = (min: number, max: number) => Math.random() * (max - min) + min;

  const showCannonConfetti = (
    originX: number,
    regularAngle: [number, number],
    logoAngle: [number, number],
  ) => {
    for (let i = 0; i < totalBursts; i++) {
      setTimeout(() => {
        // Fire regular shapes on every burst.
        confetti({
          particleCount: Math.floor(randomInRange(4, 7)),
          angle: randomInRange(...regularAngle),
          spread: randomInRange(40, 60),
          origin: { x: originX, y: 1 },
          startVelocity: randomInRange(30, 50),
          colors,
          shapes: ['circle', 'square', 'star'],
          gravity: randomInRange(0.6, 0.9),
          scalar: randomInRange(0.5, 1.5),
          drift: randomInRange(-0.4, 0.4),
          flat: false,
          disableForReducedMotion: true,
          zIndex: 9999,
        });

        if (i % 4 === 0) {
          // Fire the logo image every fourth burst.
          confetti({
            particleCount: 1,
            angle: randomInRange(...logoAngle),
            spread: randomInRange(25, 35),
            origin: { x: originX, y: 1 },
            startVelocity: randomInRange(35, 55),
            colors,
            shapes: imageShape ? [imageShape] : ['star'],
            gravity: randomInRange(0.6, 0.9),
            scalar: imageShape ? randomInRange(0.5, 1) : randomInRange(1, 2),
            drift: randomInRange(-0.4, 0.4),
            flat: Math.random() > 0.5,
            disableForReducedMotion: true,
            zIndex: 9999,
          });
        }
      }, i * burstInterval);
    }
  };

  // Fire from the left and right bottom corners.
  showCannonConfetti(0, [45, 75], [50, 70]);
  showCannonConfetti(1, [105, 135], [110, 130]);
};

export const useConfetti = () => {
  const triggerConfetti = useCallback(async (displayConfig: ConfettiDisplayConfig) => {
    try {
      const { default: confetti } = await import('canvas-confetti');

      switch (displayConfig.variant) {
        case 'cannon':
          return await triggerCannonConfetti(confetti, displayConfig);
        default:
          return triggerDefaultConfetti(confetti);
      }
    } catch (err) {
      logger.error('Failed to trigger confetti:', err);
    }
  }, []);

  return { triggerConfetti };
};
